import { useEffect, useRef, useState, type RefObject } from "react";
import { KarelIde } from "./karelIde";
import type { KarelEditor } from "../editor/karelCodeEditor";
import { useThemeStore } from "../app/stores/themeStore";
import { useCanvasRepaint } from "./useCanvasRepaint";

interface UseKarelIdeOptions {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  observe: RefObject<HTMLElement | null>;
  getEditor?: () => KarelEditor | null;
  debounceMs?: number;
  maxDimension?: number;
  onOutput?: (lines: string[]) => void;
}

interface UseKarelIdeResult {
  ideRef: RefObject<KarelIde | null>;
  ready: boolean;
}

/**
 * Creates a `KarelIde` bound to a canvas and keeps its backing store sized to
 * the observed element. Shared by the full IDE and the button-lesson canvases.
 */
export function useKarelIde(
  world: string,
  options: UseKarelIdeOptions,
): UseKarelIdeResult {
  const {
    canvasRef,
    observe,
    getEditor,
    debounceMs = 0,
    maxDimension,
  } = options;
  const ideRef = useRef<KarelIde | null>(null);
  const [ready, setReady] = useState(false);
  const onOutputRef = useRef(options.onOutput);
  onOutputRef.current = options.onOutput;

  useCanvasRepaint(() => ideRef.current?.redraw());

  useEffect(() => {
    const canvas = canvasRef.current;
    const target = observe.current;
    if (!canvas || !target) return;
    let cancelled = false;
    setReady(false);
    const ide = KarelIde(getEditor ?? (() => null), canvas, world, {
      language: useThemeStore.getState().languageMode,
      onOutput: (lines) => onOutputRef.current?.(lines),
    });
    ideRef.current = ide;

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    let lastWidth = -1;
    let lastHeight = -1;

    const applyResize = (): void => {
      const rect = target.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      if (width < 1 || height < 1) return;
      if (
        maxDimension != null &&
        (width > maxDimension || height > maxDimension)
      )
        return;
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      ide.resizeCanvas(width, height);
    };

    const observer = new ResizeObserver(() => {
      if (debounceMs > 0) {
        if (resizeTimer !== null) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyResize, debounceMs);
      } else {
        applyResize();
      }
    });
    observer.observe(target);
    void ide.ready.then(() => {
      if (cancelled) return;
      applyResize();
      setReady(true);
    });
    applyResize();

    return () => {
      cancelled = true;
      observer.disconnect();
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      ide.destroy();
      ideRef.current = null;
    };
  }, [world, canvasRef, observe, getEditor, debounceMs, maxDimension]);

  return { ideRef, ready };
}
